import React, { useEffect, useState } from "react";

// Components
import Button from "../../../../components/button";

// Styles
import styles from "./style.module.scss";

// Utility: shuffle array
function shuffle<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

interface AnswerMatchProps {
  value?: Record<string, string>;
  options: { term: string, definition: string }[];
  disabled?: boolean;
  onChange: (matches: Record<string, string>) => void;
}

const AnswerMatch: React.FC<AnswerMatchProps> = ({ value, options, disabled = false, onChange }) => {
  const [matches, setMatches] = useState<Record<string, string>>(value || {});
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);
  const [selectedDefinition, setSelectedDefinition] = useState<string | null>(null);
  const [shuffledTerms, setShuffledTerms] = useState<string[]>([]);
  const [shuffledDefinitions, setShuffledDefinitions] = useState<string[]>([]);
  const [pairColors, setPairColors] = useState<Record<string, string>>({});

  const borderColors = [
    '#10B981',
    '#3B82F6',
    '#EF4444',  
    '#F59E0B',
  ];

  // === Effects ===
  useEffect(() => {
    const _shuffledTerms = shuffle(options.map(item => item.term));
    const _shuffledDefinitions = shuffle(options.map(item => item.definition));

    setShuffledTerms(_shuffledTerms);
    setShuffledDefinitions(_shuffledDefinitions);
    
    setPairColors({});
    setMatches({});
    setSelectedTerm(null);
    setSelectedDefinition(null);
  }, [options]);

  useEffect(() => {
    if (value) setMatches(value);
  }, [value]);

  const allPairsMatched = Object.keys(matches).length === options.length;

  const getPairColor = (term: string): string => {
    return pairColors[term] || borderColors[0];
  };

  const getNextPairColor = (): string => {
    const currentPairIndex = Object.keys(pairColors).length;
    return borderColors[currentPairIndex % borderColors.length];
  };

  // === Handlers ===
  const handleTermClick = (term: string) => {
    if (allPairsMatched) return;
    if (selectedTerm === term) {
      setSelectedTerm(null);
      return;
    }
    setSelectedTerm(term);
    setSelectedDefinition(null);
  };

  const handleDefinitionClick = (definition: string) => {
    if (allPairsMatched) return;
    if (selectedDefinition === definition) {
      setSelectedDefinition(null);
      return;
    }
    setSelectedDefinition(definition);

    if (selectedTerm) {
      const pairColor = getNextPairColor();
      const newMatches = { ...matches, [selectedTerm]: definition };
      setMatches(newMatches);      
      const newPairColors = { ...pairColors, [selectedTerm]: pairColor };
      setPairColors(newPairColors);      
      setSelectedTerm(null);
      setSelectedDefinition(null);

      const isComplete = Object.keys(newMatches).length === options.length;
      
      if (isComplete) {
        onChange?.(newMatches);
      }
    }
  };

  const renderTermButtons = () => {
    return shuffledTerms.map(term => {
      const isSelected = selectedTerm === term;
      const wasMatched = matches[term];
      const isDisabled = allPairsMatched;

      let style = `${styles.optionButton}`;
      
      const selectedDefinition = matches[term];
      if (selectedDefinition) {
        const originalOption = options.find(item => item.term === term);
        const isCorrect = selectedDefinition === originalOption?.definition;
        
        if (allPairsMatched) {
          if (isCorrect) {
            style += ` ${styles.correct}`;
          } else {
            style += ` ${styles.incorrect}`;
          }
        }
      }

      const buttonProps: any = {
        key: term,
        className: style,
        disabled: isDisabled,
        onClick: () => handleTermClick(term)
      };

      if (isSelected && !matches[term] && !allPairsMatched) {
        const nextColor = getNextPairColor();
        buttonProps.style = {
          '--pair-color': nextColor
        };
        buttonProps.className = `${style} ${styles.dynamicBorder}`;
      } else if (matches[term]) {
        const pairColor = getPairColor(term);
        buttonProps.style = {
          '--pair-color': pairColor
        };
        buttonProps.className = `${style} ${styles.dynamicBorder}`;
      }

      return (
        <Button {...buttonProps} dir="auto">
          {term}
        </Button>
      );
    });
  };

  const renderDefinitionButtons = () => {
    return shuffledDefinitions.map(definition => {
      const isSelected = selectedDefinition === definition;
      const isDisabled = allPairsMatched;
      let style = `${styles.optionButton}`;
      
      const userTerm = Object.keys(matches).find(key => matches[key] === definition);
      if (userTerm) {
        const originalOption = options.find(item => item.term === userTerm);
        const isCorrect = definition === originalOption?.definition;

        if (allPairsMatched) {
          if (isCorrect) {
            style += ` ${styles.correct}`;
          } else {
            style += ` ${styles.incorrect}`;
          }
        }
      }

      const buttonProps: any = {
        key: definition,
        className: style,
        disabled: isDisabled,
        onClick: () => handleDefinitionClick(definition)
      };

      if (isSelected && selectedTerm && !allPairsMatched) {
        const nextColor = getNextPairColor();
        buttonProps.style = {
          '--pair-color': nextColor
        };
        buttonProps.className = `${style} ${styles.dynamicBorder}`;
      } else if (userTerm) {
        const pairColor = getPairColor(userTerm);
        buttonProps.style = {
          '--pair-color': pairColor
        };
        buttonProps.className = `${style} ${styles.dynamicBorder}`;
      }

      return (
        <Button {...buttonProps} dir="auto">
          {definition}
        </Button>
      );
    });
  };

  // === Main render ===
  return (
    <div className={styles.container}>
      <div className={styles.optionsContainer}>
        <div className={styles.terms}>
          {renderTermButtons()}
        </div>

        <div className={styles.definitions}>
          {renderDefinitionButtons()}
        </div>
      </div>
    </div>
  );
}

export default AnswerMatch;
